import * as React from 'react'
import { useSnapshot } from 'valtio'
import { createPortal } from 'react-dom'
import { historyState } from '../../../../store/historyState.ts'
import { dataState } from '../../../../store/dataState.ts'
import { IPet } from '../../../../interfaces/types.ts'

/**
 * Compact top bar for mobile history view.
 * Shows: pet avatar + name (tappable picker), zone toggle pill.
 */
export const MobileHistoryTopBar: React.FC = () => {
    const selectedPetId = (useSnapshot(historyState) as typeof historyState).selectedPetId
    const showSafeZones = (useSnapshot(historyState) as typeof historyState).showSafeZones
    const pets = (useSnapshot(dataState) as typeof dataState).pets
    const [pickerOpen, setPickerOpen] = React.useState(false)

    const pet = pets.find(p => p._id === selectedPetId) || null

    const selectPet = (p: IPet) => {
        historyState.selectedPetId = p._id
        setPickerOpen(false)
    }

    const petTrigger = (
        <button
            className={'flex items-center gap-2 px-2 py-1 rounded-full bg-primary-content/15 active:bg-primary-content/30 transition-colors'}
            onClick={() => setPickerOpen(true)}
        >
            {pet ? (
                <>
                    <img
                        src={pet.imageURL}
                        className={'w-8 h-8 rounded-full object-cover border-2 border-primary-content'}
                        alt={pet.petName}
                    />
                    <span className={'text-primary-content font-semibold text-sm pr-1'}>{pet.petName}</span>
                </>
            ) : (
                <span className={'text-primary-content font-semibold text-sm px-2 py-1'}>Select Pet</span>
            )}
        </button>
    )

    const pickerModal = pickerOpen ? createPortal(
        <div
            className={'fixed inset-0 z-[100000] flex items-center justify-center bg-black/70'}
            data-theme={'gibi'}
            onClick={(e) => { if (e.target === e.currentTarget) setPickerOpen(false) }}
        >
            <div className={'flex flex-col items-center'}>
                <div className={'text-3xl text-white pb-3'}>Select Pet</div>
                <div className={'grid grid-cols-3 gap-3 px-6'}>
                    {pets.map(p => (
                        <button
                            key={p._id}
                            className={`flex flex-col items-center gap-1 p-2 rounded-xl border-2 transition-colors ${
                                p._id === selectedPetId
                                    ? 'border-secondary bg-secondary/20'
                                    : 'border-white/30 bg-white/10 active:bg-white/20'
                            }`}
                            onClick={() => selectPet(p)}
                        >
                            <img
                                src={p.imageURL}
                                className={'w-16 h-16 rounded-full object-cover border-2 border-white'}
                                alt={p.petName}
                            />
                            <span className={'text-white text-sm font-semibold'}>{p.petName}</span>
                        </button>
                    ))}
                </div>
                <button
                    className={'btn btn-ghost text-white mt-4'}
                    onClick={() => setPickerOpen(false)}
                >
                    Cancel
                </button>
            </div>
        </div>,
        document.body
    ) : null

    return (
        <>
            <div className={'bg-primary px-3 py-2 flex items-center justify-between'}>
                {petTrigger}

                {/* Safe Zones toggle */}
                <label className={'flex items-center gap-2 cursor-pointer'} onClick={() => historyState.showSafeZones = !showSafeZones}>
                    <span className={'text-primary-content text-sm font-medium'}>Safe Zones</span>
                    <div className={`relative w-10 h-5 rounded-full transition-colors ${showSafeZones ? 'bg-white' : 'bg-primary-content/30'}`}>
                        <div className={`absolute top-0.5 w-4 h-4 rounded-full shadow transition-all ${showSafeZones ? 'left-[17px] bg-primary' : 'left-0.5 bg-white'}`} />
                    </div>
                </label>
            </div>
            {pickerModal}
        </>
    )
}
